import React, { useMemo } from 'react';
import { Autocomplete, Box, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { TpaTransitionFormProps, TpaForClient } from '../utils/DataTypes';
import { CLIENT_SPLIT_STRINGS } from '../constants';
import { useNotify } from 'react-admin';

const TpaTransitionForm = ({
  formData,
  handleInputChange,
  tpaList,
  isTpaLoading,
  associatedTpa,
  onTpaSelect,
}: TpaTransitionFormProps) => {
  const notify = useNotify();
  const oldTpaValue = useMemo(() => {
    if (isTpaLoading) return null;

    const foundByName = tpaList.find((tpa) => tpa.vendor_name === formData.oldTpaName);
    if (foundByName) return foundByName;

    if (associatedTpa) {
      return tpaList.find((tpa) => tpa.vendor_number === associatedTpa.vendor_number) || null;
    }
    return null;
  }, [formData.oldTpaName, associatedTpa, tpaList, isTpaLoading]);

  const newTpaValue = useMemo(() => {
    if (isTpaLoading) return null;

    const foundByName = tpaList.find((tpa) => tpa.vendor_name === formData.newTpaName);
    if (foundByName) return foundByName;

    if (associatedTpa) {
      return tpaList.find((tpa) => tpa.vendor_number === associatedTpa.vendor_number) || null;
    }

    return null;
  }, [formData.newTpaName, associatedTpa, tpaList, isTpaLoading]);

  const filterTpaOptions = (options: TpaForClient[], state: { inputValue: string }) => {
    if (state.inputValue.length === 0) {
      return options;
    }
    const lowerInput = state.inputValue.toLowerCase();
    return options.filter(
      (tpa) =>
        tpa.vendor_name.toLowerCase().includes(lowerInput) ||
        (tpa.vendor_number && String(tpa.vendor_number).includes(lowerInput))
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {CLIENT_SPLIT_STRINGS.TPA_FORM.HEADING}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, mb: 2 }}>
          <TextField
            label={CLIENT_SPLIT_STRINGS.TPA_FORM.OLD_CLIENT_NAME_LABEL}
            name="clientName"
            value={formData.clientName}
            onChange={handleInputChange}
            variant="standard"
            required
          />
          <TextField
            label={CLIENT_SPLIT_STRINGS.TPA_FORM.CLIENT_NUMBER_LABEL}
            name="clientNumber"
            value={formData.clientNumber}
            variant="standard"
            disabled
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, mb: 2 }}>
          <TextField
            label={CLIENT_SPLIT_STRINGS.TPA_FORM.NEW_CLIENT_NAME_LABEL}
            name="newBillingClientName"
            value={formData.newBillingClientName}
            onChange={handleInputChange}
            variant="standard"
            required
          />
          <TextField
            label={CLIENT_SPLIT_STRINGS.TPA_FORM.GP_ID_LABEL}
            name="gpAccountingClientId"
            value={formData.gpAccountingClientId}
            onChange={handleInputChange}
            variant="standard"
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          <Autocomplete
            options={tpaList}
            value={oldTpaValue}
            loading={isTpaLoading}
            onChange={(event, newValue) => onTpaSelect('oldTpaName', newValue)}
            getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_number})`}
            isOptionEqualToValue={(option, value) => option.vendor_number === value.vendor_number}
            renderOption={(props, option) => (
              <li {...props} key={option.vendor_number}>
                {`${option.vendor_name} (${option.vendor_number})`}
              </li>
            )}
            filterOptions={filterTpaOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label={CLIENT_SPLIT_STRINGS.TPA_FORM.OLD_TPA_NAME_LABEL}
                variant="standard"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isTpaLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label={CLIENT_SPLIT_STRINGS.TPA_FORM.OLD_TPA_BILLING_NUMBER_LABEL}
            name="oldTpaBillingNumber"
            value={formData.oldTpaBillingNumber}
            onChange={handleInputChange}
            variant="standard"
          />
          <Autocomplete
            options={tpaList}
            value={newTpaValue}
            loading={isTpaLoading}
            onChange={(event, newValue) => {
              if (newValue && oldTpaValue && newValue.vendor_number === oldTpaValue.vendor_number) {
                notify('New TPA cannot be the same as the Old TPA.', { type: 'warning' });
              } else {
                onTpaSelect('newTpaName', newValue);
              }
            }}
            getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_number})`}
            isOptionEqualToValue={(option, value) => option.vendor_number === value.vendor_number}
            renderOption={(props, option) => (
              <li {...props} key={option.vendor_number}>
                {`${option.vendor_name} (${option.vendor_number})`}
              </li>
            )}
            filterOptions={filterTpaOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label={CLIENT_SPLIT_STRINGS.TPA_FORM.NEW_TPA_NAME_LABEL}
                variant="standard"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isTpaLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label={CLIENT_SPLIT_STRINGS.TPA_FORM.NEW_TPA_BILLING_NUMBER_LABEL}
            name="newTpaBillingNumber"
            value={formData.newTpaBillingNumber}
            onChange={handleInputChange}
            variant="standard"
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default TpaTransitionForm;
